import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ApiError } from '@immoaktas/api-client';
import { useTranslation } from 'react-i18next';
import { Button, IconButton } from './buttons';
import { TextInput } from './fields';
import { Icon } from './icons';
import { errorText } from './states';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Modal dialog: scrim + scaled panel (docs/10), focus trap, Escape closes, focus returns to the opener. */
/**
 * `size="xl"` + `sectioned` give the large two-part dialogs (design/MieterEinladen, MieteAnpassen): fixed head with
 * `eyebrow`, scrolling body (wrap columns in `.dialog-split`), foot with `footNote` left and actions right.
 */
/** Open dialogs, innermost last: only the top one reacts to Esc and Tab, the scroll lock ends with the last one. */
const dialogStack: object[] = [];

/** Inline "Änderungen verwerfen?" bar (no browser dialog): used by a dirty Dialog and by the page leave guard. */
export function DiscardConfirm({ title, text, confirmLabel, onConfirm, onKeep }: { title?: string; text?: string; confirmLabel?: string; onConfirm: () => void; onKeep: () => void }) {
  const { t } = useTranslation('common');
  const keep = useRef<HTMLButtonElement>(null);
  useEffect(() => keep.current?.focus(), []);
  return (
    <div className="banner warn discard-confirm" role="alertdialog" aria-label={title ?? t('ui.discard.question')}>
      <Icon name="alert" size={18} strokeWidth={2} />
      <div className="stack tight">
        <strong>{title ?? t('ui.discard.question')}</strong>
        <span className="small">{text ?? t('ui.discard.text')}</span>
        <div className="row" style={{ gap: 8 }}><Button ref={keep} size="sm" variant="secondary" onClick={onKeep}>{t('ui.discard.keep')}</Button><Button size="sm" variant="danger" onClick={onConfirm}>{confirmLabel ?? t('ui.discard.confirm')}</Button></div>
      </div>
    </div>
  );
}

/**
 * `dirty`: the dialog holds unsaved input. Backdrop clicks then do nothing and Esc / ✕ first ask inline whether to
 * discard (Esc again keeps editing). Explicit footer buttons (Abbrechen) still call onClose directly.
 */
export function Dialog({ open, onClose, title, sub, children, actions, wide, dismissible = true, size, sectioned, eyebrow, footNote, icon, dirty = false }: { open: boolean; onClose: () => void; title: string; sub?: ReactNode; children: ReactNode; actions?: ReactNode; wide?: boolean; dismissible?: boolean; size?: 'xl'; sectioned?: boolean; eyebrow?: ReactNode; footNote?: ReactNode; /** Square icon left of the title block (design/Konflikt). */ icon?: ReactNode; dirty?: boolean }) {
  const { t } = useTranslation('common');
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    if (!open || !dirty) setConfirming(false);
  }, [open, dirty]);
  const requestClose = () => (dirty ? setConfirming(true) : onClose());
  const close = useRef(requestClose);
  close.current = requestClose;
  const asking = useRef(confirming);
  asking.current = confirming;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const panel = ref.current;
    const layer = {};
    dialogStack.push(layer);
    (panel?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled])') ?? panel?.querySelector<HTMLElement>('.actions button:not([disabled])') ?? panel)?.focus();
    document.body.classList.add('scroll-locked');
    const onKey = (e: KeyboardEvent) => {
      // a dialog opened from this one (e.g. one row of a list) handles the keys itself
      if (dialogStack[dialogStack.length - 1] !== layer) return;
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation();
        if (asking.current) setConfirming(false);
        else close.current();
      }
      if (e.key === 'Tab' && panel) {
        const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      dialogStack.splice(dialogStack.indexOf(layer), 1);
      if (!dialogStack.length) document.body.classList.remove('scroll-locked');
      prev?.focus();
    };
  }, [open, dismissible]);
  if (!open) return null;
  // Portal to <body>: an ancestor with a transform (cards animate in with iaIn) would otherwise become the containing
  // block of the fixed backdrop and the dialog would open inside that card instead of over the page.
  return createPortal(
    <div className="backdrop" onMouseDown={(e) => dismissible && !dirty && e.target === e.currentTarget && onClose()}>
      <div className={['dialog', wide ? 'wide' : '', size ?? '', sectioned ? 'sectioned' : ''].filter(Boolean).join(' ')} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref} tabIndex={-1}>
        <div className="dialog-head">
          {icon}
          <div className={icon ? 'dialog-titles' : undefined}>
            {eyebrow && <span className="dialog-eyebrow">{eyebrow}</span>}
            <h2 id={titleId}>{title}</h2>
            {sub && <p className="dialog-sub">{sub}</p>}
          </div>
          {dismissible && <IconButton className="dialog-close" icon="close" label={t('close')} onClick={requestClose} />}
        </div>
        {confirming && <DiscardConfirm onConfirm={() => { setConfirming(false); onClose(); }} onKeep={() => setConfirming(false)} />}
        {sectioned ? <div className="dialog-body">{children}</div> : children}
        {(actions || footNote) && <div className="actions">{footNote && <span className="dialog-foot-note">{footNote}</span>}{actions}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Confirm with typed confirmation for irreversible actions (e.g. finalize). */
export function ConfirmDialog({ open, onClose, onConfirm, title, text, confirmWord, label, danger, loading }: { open: boolean; onClose: () => void; onConfirm: () => void; title: string; text: ReactNode; confirmWord?: string; label?: string; danger?: boolean; loading?: boolean }) {
  const { t } = useTranslation('common');
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} title={title} actions={<><Button variant="secondary" onClick={onClose}>{t('cancel')}</Button><Button variant={danger ? 'danger' : 'primary'} disabled={!!confirmWord && typed.trim() !== confirmWord} loading={loading} onClick={onConfirm}>{label ?? t('confirm')}</Button></>}>
      <div className="stack">
        <div>{text}</div>
        {confirmWord && <TextInput label={t('ui.confirm.typeToConfirm', { word: confirmWord })} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />}
      </div>
    </Dialog>
  );
}

// ───────────── toasts ─────────────
export type ToastTone = 'ok' | 'warn' | 'err';
export type ToastAction = { label: string; run: () => void };
type Toast = { id: number; text: string; tone: ToastTone; action?: ToastAction };
type Push = (t: Omit<Toast, 'id'> & { duration?: number }) => void;
const ToastCtx = createContext<Push>(() => undefined);
const DURATION: Record<ToastTone, number | null> = { ok: 4000, warn: 6000, err: null };
/** A toast with an action ("Rückgängig") stays longer: time to reach the button by keyboard or screen reader. */
const ACTION_DURATION = 10_000;
const MAX = 3;

/**
 * Toasts bottom right: success 4 s, warning 6 s, error stays until closed, max 3 stacked (design/Zustaende). With an
 * action 10 s; hovering or focusing a toast pauses its timer (WCAG 2.2.1), leaving restarts it.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const [list, setList] = useState<Toast[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const durations = useRef(new Map<number, number>());
  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    durations.current.delete(id);
    setList((l) => l.filter((x) => x.id !== id));
  }, []);
  const push = useCallback<Push>(({ duration, ...toast }) => {
    const id = Date.now() + Math.random();
    setList((l) => [...l, { ...toast, id }].slice(-MAX));
    const base = duration === undefined ? DURATION[toast.tone] : duration;
    const ms = base && toast.action && duration === undefined ? Math.max(base, ACTION_DURATION) : base;
    if (ms) {
      durations.current.set(id, ms);
      timers.current.set(id, setTimeout(() => dismiss(id), ms));
    }
  }, [dismiss]);
  const pause = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
  }, []);
  const resume = useCallback((id: number) => {
    const ms = durations.current.get(id);
    if (ms && !timers.current.has(id)) timers.current.set(id, setTimeout(() => dismiss(id), ms));
  }, [dismiss]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite" aria-relevant="additions">
        {list.map((x) => (
          <div key={x.id} className={`toast ${x.tone === 'ok' ? '' : x.tone}`} role={x.tone === 'err' ? 'alert' : 'status'}
            onMouseEnter={() => pause(x.id)} onMouseLeave={() => resume(x.id)} onFocus={() => pause(x.id)}
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) resume(x.id); }}>
            <span className="toast-icon" aria-hidden><Icon name={x.tone === 'ok' ? 'check' : x.tone === 'warn' ? 'alert' : 'close'} size={14} strokeWidth={2.4} /></span>
            <span className="toast-text">{x.text}</span>
            {x.action && <button type="button" className="toast-action" onClick={() => { x.action!.run(); dismiss(x.id); }}>{x.action.label}</button>}
            {(x.tone === 'err' || !x.action) && <button type="button" className="toast-close" aria-label={t('ui.toast.close')} onClick={() => dismiss(x.id)}><Icon name="close" size={16} strokeWidth={2} /></button>}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => {
  const push = useContext(ToastCtx);
  const { t } = useTranslation('common');
  return useMemo(
    () => ({
      ok: (text: string, action?: ToastAction) => push({ text, tone: 'ok', action }),
      warn: (text: string, action?: ToastAction) => push({ text, tone: 'warn', action }),
      /** err() accepts an error (translated via errorText) or an already translated message. */
      err: (e: unknown, action?: ToastAction) => push({ text: typeof e === 'string' ? e : errorText(e, t), tone: 'err', action }),
      show: push,
    }),
    [push, t],
  );
};

/** 409 conflict: shows the current server state (docs/10 Konflikt). */
export function ConflictNotice({ error, onReload }: { error: unknown; onReload: () => void }) {
  const { t } = useTranslation('common');
  if (!(error instanceof ApiError) || error.code !== 'CONFLICT') return null;
  return (
    <div className="banner warn" role="alert">
      <Icon name="alert" size={18} strokeWidth={2} />
      <div className="stack tight">
        <strong>{t('ui.conflict.title')}</strong>
        <span className="small">{errorText(error, t)} {t('ui.conflict.hint')}</span>
        <div><Button size="sm" variant="secondary" onClick={onReload}>{t('ui.conflict.reload')}</Button></div>
      </div>
    </div>
  );
}

/** Drop zone with file picker; `hint` replaces the default formats line (receipts: PDF, JPG …) for other file kinds such as CSV. */
export function FileDrop({ onFiles, accept, multiple = true, label, hint }: { onFiles: (f: File[]) => void; accept?: string; multiple?: boolean; label?: string; hint?: ReactNode }) {
  const { t } = useTranslation('common');
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  return (
    <div
      className={`filedrop ${over ? 'over' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => input.current?.click()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current?.click(); } }}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onFiles([...e.dataTransfer.files]); }}
    >
      <input ref={input} type="file" hidden accept={accept} multiple={multiple} onChange={(e) => { onFiles([...(e.target.files ?? [])]); e.target.value = ''; }} />
      <Icon name="upload" size={26} strokeWidth={1.8} />
      <p><strong>{label ?? t('ui.fileDrop.label')}</strong></p>
      <p className="small muted">{hint ?? t('ui.fileDrop.formats', { maxMb: 25 })}</p>
    </div>
  );
}
